import * as React from 'react'
import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import { Field, useFormikContext } from 'formik'

import { Note } from '../Fields/Note'
import { OwnerSelect } from '@user/components/Owner'
import { TypeCommonFields } from '../Fields/TypeCommonFields'
import { SalesTeamSelect } from '@user/components/Leads/AddLeadPage/FormFields/SalesTeamSelect/SalesTeamSelect'
import { SalesFunnelSelect } from '@user/components/Leads/AddLeadPage/FormFields/SalesFunnelSelect/SalesFunnelSelect'
import { OpportunityStageSelect } from '@user/components/Opportunities/AddOpportunityPage/AddOpportunityForm/FormComponents/OpportunityStageSelect/OpportunityStageSelect'
import { InfoTooltip } from '@sales-center/components'
import { CurrencyFieldWithNegativePrice } from '@user/components/Formik/CurrencyFieldWithNegativePrice'

import { ENTITY_TYPES } from '@sales-center/common'
import { ImportUtilityModel } from '../../typings'

import { useFieldOptionsData } from '../../hooks/useFieldOptionsData'
import Typography from '@mui/material/Typography'
import { ServiceFrequencySelect } from '../Fields/ServiceFrequencySelect'
import { useImportData } from '../hooks/useImportData'
import { toast } from '@sales-center/components'
import CircularProgress from '@mui/material/CircularProgress'
import { useDefaultHandlers } from '../hooks/useDefaultHandlers'
import { ImportDatePicker } from '../Fields/ImportDatePicker'
import { MuiTextFieldFormik } from '@user/components/Formik'
import { ServicesOrProducts } from '../Fields/ServicesOrProducts'

export function OpportunityFields() {
  const { values, errors } = useFormikContext<ImportUtilityModel>()

  const { loading, fail } = useImportData()

  const { allSalesTeams, salesFunnels, serviceFrequencies } =
    useFieldOptionsData(values)

  const { onChangeSalesFunnel, onChangeOwnerShip, onChangeSalesTeam } =
    useDefaultHandlers()

  useEffect(() => {
    if (fail) {
      toast.error('Unable to retrieve Import Lookup data.')
    }
  }, [fail])

  return (
    <Box mb={5}>
      {loading ? (
        <>
          <Box pt={4.8} textAlign="center">
            <CircularProgress />
          </Box>
        </>
      ) : (
        <>
          <Note />
          <TypeCommonFields />
          <Grid mt={0.1} container spacing={2}>
            <Grid item xs={12}>
              <SalesTeamSelect
                name="salesTeamId"
                label="Sales Team"
                optionList={allSalesTeams}
                onChange={onChangeSalesTeam}
                disabled={false}
                required={false}
              />
            </Grid>
            {/* //Owner */}
            <Grid item xs={12} mt={-1.5} mb={-1}>
              <OwnerSelect
                entityType={ENTITY_TYPES.Lead}
                salesTeamId={`${values.salesTeamId}`}
                name="assigneeId"
                useAdditionalMargin={true}
                errorText={''}
                testId="importUtilityOwner"
                required={false}
                disabled={false}
                defaultValue={''}
                onChange={onChangeOwnerShip}
              />
            </Grid>
            <Grid item xs={12}>
              <SalesFunnelSelect
                name="salesFunnelId"
                label="Sales Funnel"
                salesFunnels={salesFunnels}
                disabled={false}
                onChange={onChangeSalesFunnel}
                required={false}
              />
            </Grid>

            {/* Opportunity Stage */}
            <Grid item xs={12}>
              <OpportunityStageSelect
                name="opportunityStageId"
                disableOptions={false}
                onClosedWonChange={() => {}}
                required={false}
                hideClosed={true}
              />
            </Grid>
            <Grid item xs={12}>
              <ServicesOrProducts name="service" />
            </Grid>
            <Grid item xs={12}>
              <ServiceFrequencySelect
                name="frequency"
                options={serviceFrequencies}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1} mb={2} mt={0.5}>
            <Grid container item xs direction="column">
              <Grid item container alignItems="center">
                <Typography variant="body1" sx={{ mr: 0.5 }}>
                  Initial
                </Typography>

                <Stack
                  alignItems={'center'}
                  justifyContent={'center'}
                  color={(theme) => theme.palette.action.active}
                >
                  <InfoTooltip
                    infoText="Price for initial service"
                    tooltipPlacement="top"
                    fontSize={20}
                    iconColor="inherit"
                  />
                </Stack>
              </Grid>
              <CurrencyFieldWithNegativePrice
                name="initialPrice"
                required={false}
                margin="dense"
                // onUpdate={onUpdate}
              />
            </Grid>
            <Grid container item xs direction="column">
              <Grid item container alignItems="center">
                <Typography variant="body1" sx={{ mr: 0.5 }}>
                  Recurring
                </Typography>

                <Stack
                  alignItems={'center'}
                  justifyContent={'center'}
                  color={(theme) => theme.palette.action.active}
                >
                  <InfoTooltip
                    infoText="Price for recurring service"
                    tooltipPlacement="top"
                    fontSize={20}
                    iconColor="inherit"
                  />
                </Stack>
              </Grid>
              <CurrencyFieldWithNegativePrice
                name="recurringPrice"
                required
                margin="dense"
                // onUpdate={onUpdate}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} mt={0} mb={2}>
            <Field
              name="quantity"
              label="Quantity"
              component={MuiTextFieldFormik}
              type="number"
              fullWidth
              size="small"
              sx={{ marginTop: '0px' }}
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ImportDatePicker
                label="Expected Closing Date"
                name="expectedClosingDate"
              />
            </Grid>
            <Grid item xs={12}>
              <ImportDatePicker
                label="Next Action Date"
                name="nextActionDate"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
